import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { productsApi, leadsApi, integrationsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Package, ArrowLeft, Save, Plus, Trash2, ExternalLink,
  Bot, Target, Users, Code, Activity, Settings as SettingsIcon, Sparkles,
  Workflow as WorkflowIcon, Beaker, CheckCircle2, XCircle, AlertCircle, Clock, Play,
  TestTube2, ChevronRight
} from 'lucide-react';

const AGENT_LABELS = {
  lead_intake: 'Lead Intake Agent',
  qualification: 'Qualification Agent',
  product_fit: 'Product Fit Agent',
  trial_follow_up: 'Trial Follow-up Agent',
  booking: 'Booking Agent',
  discovery: 'Discovery Agent',
  proposal: 'Proposal Agent',
  objection_handling: 'Objection Handling Agent',
  human_handoff: 'Human Handoff Agent',
  closing_follow_up: 'Closing Follow-up Agent'
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [product, setProduct] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [events, setEvents] = useState([]);
  const [sandboxSaving, setSandboxSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [p, s] = await Promise.all([
        productsApi.get(id),
        productsApi.getStats(id).catch(() => ({ data: null }))
      ]);
      setProduct(p.data);
      setStats(s.data);
    } catch (e) {
      toast.error('Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const loadLeads = async () => {
    try {
      const r = await leadsApi.list({ product_id: id, limit: 100 });
      setLeads(r.data || []);
    } catch {}
  };

  const loadEvents = async () => {
    try {
      const r = await integrationsApi.listEvents({ product_id: id, limit: 50 });
      setEvents(r.data || []);
    } catch {}
  };

  useEffect(() => {
    load();
    loadLeads();
    loadEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const toggleSandbox = async (val) => {
    if (!isAdmin()) return;
    setSandboxSaving(true);
    try {
      await productsApi.update(id, { sandbox_mode: val });
      setProduct((p) => ({ ...p, sandbox_mode: val }));
      toast.success(val ? 'Sandbox Mode enabled — no real messages will be sent' : 'Sandbox Mode disabled');
    } catch (e) {
      toast.error('Failed to update Sandbox Mode');
    } finally {
      setSandboxSaving(false);
    }
  };

  if (loading || !product) {
    return <div className="p-6 text-slate-500">Loading...</div>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="product-detail-page">
      <Button variant="ghost" size="sm" onClick={() => navigate('/products')} className="mb-3">
        <ArrowLeft className="w-4 h-4 mr-2" /> Back to Products
      </Button>

      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center flex-shrink-0">
            <Package className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{product.name}</h1>
            <p className="text-sm text-slate-500 mt-1">
              <span className="font-mono">{product.product_code}</span>
              {product.category && <> · {product.category}</>}
            </p>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <Badge variant={product.is_active ? 'default' : 'secondary'}>
                {product.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {product.trial_available && (
                <Badge variant="outline">
                  <Sparkles className="w-3 h-3 mr-1" />
                  Trial {product.trial_length_days ? `${product.trial_length_days}d` : ''}
                </Badge>
              )}
              {product.sandbox_mode && (
                <Badge className="bg-amber-100 text-amber-800 border-amber-200 border" data-testid="sandbox-badge">
                  <Beaker className="w-3 h-3 mr-1" />
                  Sandbox Mode
                </Badge>
              )}
              {product.website_url && (
                <a
                  href={product.website_url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Website
                </a>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 ml-4">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg border bg-slate-50">
            <Beaker className={`w-4 h-4 ${product.sandbox_mode ? 'text-amber-600' : 'text-slate-400'}`} />
            <div className="text-right">
              <Label className="text-xs text-slate-500 m-0 leading-tight cursor-pointer" htmlFor="sandbox-toggle">
                Sandbox Mode
              </Label>
              <div className="text-[10px] text-slate-400 leading-tight">No real sends</div>
            </div>
            <Switch
              id="sandbox-toggle"
              checked={!!product.sandbox_mode}
              onCheckedChange={toggleSandbox}
              disabled={sandboxSaving || !isAdmin()}
              data-testid="sandbox-toggle"
            />
          </div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="leads" data-testid="tab-leads">Leads</TabsTrigger>
          <TabsTrigger value="agents" data-testid="tab-agents">Agents</TabsTrigger>
          <TabsTrigger value="guidance-rules" data-testid="tab-guidance-rules">AI Guidance Rules</TabsTrigger>
          <TabsTrigger value="workflow" data-testid="tab-workflow">Workflow</TabsTrigger>
          <TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab product={product} stats={stats} />
        </TabsContent>

        <TabsContent value="leads">
          <LeadsTab leads={leads} navigate={navigate} />
        </TabsContent>

        <TabsContent value="agents">
          <AgentsTab product={product} reload={load} isAdmin={isAdmin()} />
        </TabsContent>

        <TabsContent value="guidance-rules">
          <GuidanceRulesTab product={product} reload={load} isAdmin={isAdmin()} />
        </TabsContent>

        <TabsContent value="workflow">
          <WorkflowTab productId={id} />
        </TabsContent>

        <TabsContent value="integrations">
          <IntegrationsTab product={product} events={events} reloadEvents={loadEvents} />
        </TabsContent>

        <TabsContent value="settings">
          <SettingsTab product={product} reload={load} isAdmin={isAdmin()} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ OVERVIEW ============

function OverviewTab({ product, stats }) {
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle className="text-base">About</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div>
            <Label className="text-xs text-slate-500">Description</Label>
            <p className="text-slate-700">{product.description || '—'}</p>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Target Audience</Label>
            <p className="text-slate-700">{product.target_audience || '—'}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Key Benefits</Label>
              {(product.key_benefits || []).length === 0 ? (
                <p className="text-slate-400 text-sm">None</p>
              ) : (
                <ul className="list-disc list-inside text-slate-700 text-sm">
                  {product.key_benefits.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Pain Points Solved</Label>
              {(product.pain_points_solved || []).length === 0 ? (
                <p className="text-slate-400 text-sm">None</p>
              ) : (
                <ul className="list-disc list-inside text-slate-700 text-sm">
                  {product.pain_points_solved.map((b, i) => <li key={i}>{b}</li>)}
                </ul>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs text-slate-500">Default Funnel</Label>
              <p className="text-slate-700">{product.default_funnel || '—'}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Default CTA</Label>
              <p className="text-slate-700">{product.default_cta || '—'}</p>
            </div>
          </div>
          {product.compliance_text && (
            <div className="pt-2 border-t">
              <Label className="text-xs text-slate-500">Compliance / Disclaimer</Label>
              <p className="text-slate-600 text-xs italic">{product.compliance_text}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Performance</CardTitle>
          <CardDescription className="text-xs">
            Snapshot of leads, AI activity and revenue
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm" data-testid="product-stats">
          <StatRow label="Active Leads" value={stats?.active_leads ?? 0} />
          <StatRow label="AI Actions" value={stats?.ai_actions ?? 0} />
          <StatRow label="Pending Actions" value={stats?.pending_actions ?? 0} />
          <StatRow label="Booked Calls" value={stats?.booked_calls ?? 0} />
          <StatRow label="Proposals" value={stats?.proposals ?? 0} />
          <StatRow label="Won Deals" value={stats?.won_deals ?? 0} />
          <StatRow label="Conversion Rate" value={`${stats?.conversion_rate ?? 0}%`} />
          <StatRow label="Credits Used" value={(stats?.credits_used ?? 0).toFixed(2)} />
          <StatRow label="Revenue" value={`$${(stats?.revenue ?? 0).toLocaleString()}`} bold />
        </CardContent>
      </Card>
    </div>
  );
}

const StatRow = ({ label, value, bold }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500">{label}</span>
    <span className={bold ? 'font-semibold text-slate-900' : 'text-slate-700'}>{value}</span>
  </div>
);

// ============ LEADS ============

function LeadsTab({ leads, navigate }) {
  if (leads.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-slate-500">
          <Users className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          No leads assigned to this product yet.
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Stage</th>
              <th className="text-left px-4 py-3">Goal</th>
              <th className="text-left px-4 py-3">Score</th>
            </tr>
          </thead>
          <tbody>
            {leads.map(l => (
              <tr
                key={l.id}
                className="border-t hover:bg-slate-50 cursor-pointer"
                onClick={() => navigate(`/leads/${l.id}`)}
                data-testid={`product-lead-row-${l.id}`}
              >
                <td className="px-4 py-3">
                  <div className="font-medium">{l.name}</div>
                  <div className="text-xs text-slate-500">{l.email}</div>
                </td>
                <td className="px-4 py-3">
                  <Badge variant="outline" className="text-xs capitalize">
                    {(l.stage || '').replace(/_/g, ' ')}
                  </Badge>
                </td>
                <td className="px-4 py-3 text-slate-600">{l.goal || '—'}</td>
                <td className="px-4 py-3">{l.lead_score ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

// ============ AGENTS ============

function AgentsTab({ product, reload, isAdmin }) {
  const agents = product.agents || [];
  const [runtime, setRuntime] = useState({});

  const loadRuntime = async () => {
    try {
      const r = await productsApi.agentsRuntime(product.id);
      const byAgent = {};
      (r.data || []).forEach((a) => { byAgent[a.agent_type] = a; });
      setRuntime(byAgent);
    } catch {}
  };

  useEffect(() => {
    loadRuntime();
    const interval = setInterval(loadRuntime, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.id]);

  return (
    <div className="space-y-3" data-testid="agents-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Configure each agent's behavior for this product. Runtime status auto-refreshes every 30s.
        </p>
        <Button size="sm" variant="outline" onClick={loadRuntime} data-testid="refresh-runtime-btn">
          <Activity className="w-4 h-4 mr-2" /> Refresh Runtime
        </Button>
      </div>
      {agents.map((a) => (
        <AgentRow
          key={a.agent_type}
          product={product}
          agent={a}
          reload={async () => { await reload(); await loadRuntime(); }}
          runtime={runtime[a.agent_type]}
          isAdmin={isAdmin}
        />
      ))}
    </div>
  );
}

const STATUS_STYLES = {
  Active: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  Idle: 'bg-slate-100 text-slate-600 border-slate-200',
  Paused: 'bg-slate-200 text-slate-700 border-slate-300',
  Processing: 'bg-blue-100 text-blue-800 border-blue-200',
  'Manual Review': 'bg-amber-100 text-amber-800 border-amber-200',
  Error: 'bg-red-100 text-red-800 border-red-200'
};

const TRIGGER_EVENTS = [
  { value: 'lead_created', label: 'Lead created' },
  { value: 'trial_started', label: 'Trial started' },
  { value: 'trial_expiring', label: 'Trial expiring' },
  { value: 'demo_requested', label: 'Demo requested' },
  { value: 'proposal_sent', label: 'Proposal sent' },
  { value: 'pricing_viewed', label: 'Pricing viewed' },
  { value: 'call_scheduled', label: 'Call scheduled' },
  { value: 'call_completed', label: 'Call completed' },
  { value: 'manual', label: 'Manual only' },
  { value: 'custom', label: 'Custom / external event' },
];

const STOP_CONDITION_OPTIONS = [
  { value: 'lead_replied', label: 'Lead replied' },
  { value: 'demo_booked', label: 'Demo booked' },
  { value: 'deal_won', label: 'Deal won' },
  { value: 'deal_lost', label: 'Deal lost' },
  { value: 'lead_opted_out', label: 'Lead opted out' },
  { value: 'max_repeats_reached', label: 'Max repeats reached' },
];

function formatDelay(mins) {
  const m = Number(mins) || 0;
  if (m === 0) return 'no delay';
  if (m < 60) return `${m}m`;
  const h = m / 60;
  if (h < 24) return `${h % 1 === 0 ? h : h.toFixed(1)}h`;
  const d = h / 24;
  return `${d % 1 === 0 ? d : d.toFixed(1)}d`;
}

function AgentRow({ product, agent, reload, runtime, isAdmin }) {
  const [open, setOpen] = useState(false);
  const [cfg, setCfg] = useState(agent);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setCfg(agent); }, [agent]);

  const toggleChannel = (ch) => {
    const set = new Set(cfg.allowed_channels || []);
    if (set.has(ch)) set.delete(ch); else set.add(ch);
    setCfg({ ...cfg, allowed_channels: Array.from(set) });
  };

  const save = async () => {
    setSaving(true);
    try {
      await productsApi.updateAgent(product.id, agent.agent_type, {
        agent_type: agent.agent_type,
        enabled: cfg.enabled,
        review_mode: cfg.review_mode,
        allowed_channels: cfg.allowed_channels?.length ? cfg.allowed_channels : ['email'],
        follow_up_frequency_hours: parseInt(cfg.follow_up_frequency_hours, 10) || 48,
        max_actions_per_lead_per_day: parseInt(cfg.max_actions_per_lead_per_day, 10) || 3,
        max_credits_per_lead_per_day: parseFloat(cfg.max_credits_per_lead_per_day) || 10,
        prompt_instructions: cfg.prompt_instructions || null,
        // Scheduling visibility
        trigger_event: cfg.trigger_event || 'lead_created',
        initial_delay_minutes: cfg.initial_delay_minutes === '' || cfg.initial_delay_minutes == null
          ? 0
          : parseInt(cfg.initial_delay_minutes, 10) || 0,
        repeat_frequency_hours: cfg.repeat_frequency_hours === '' || cfg.repeat_frequency_hours == null
          ? null
          : parseInt(cfg.repeat_frequency_hours, 10),
        max_repeats: cfg.max_repeats === '' || cfg.max_repeats == null
          ? 1
          : parseInt(cfg.max_repeats, 10) || 1,
        stop_conditions: cfg.stop_conditions && cfg.stop_conditions.length
          ? cfg.stop_conditions
          : ['lead_replied', 'demo_booked', 'deal_won', 'deal_lost', 'lead_opted_out', 'max_repeats_reached']
      });
      toast.success('Agent updated');
      setOpen(false);
      await reload();
    } catch (e) {
      // Surface the real reason: backend detail, HTTP status, or network error
      const status = e?.response?.status;
      let detail = e?.response?.data?.detail;
      if (Array.isArray(detail)) {
        // Pydantic 422 error array → first message
        detail = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
      }
      const msg = detail
        ? `Failed to update agent: ${detail}`
        : status
          ? `Failed to update agent (HTTP ${status})`
          : `Failed to update agent: ${e?.message || 'network error'}`;
      // eslint-disable-next-line no-console
      console.error('[updateAgent] failed', { status, data: e?.response?.data, error: e });
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid={`agent-row-${agent.agent_type}`}>
      <CardContent className="py-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Bot className="w-5 h-5 text-indigo-500 flex-shrink-0" />
          <div className="min-w-0">
            <div className="font-medium">{AGENT_LABELS[agent.agent_type] || agent.agent_type}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
              <Badge variant={agent.enabled ? 'default' : 'secondary'} className="text-xs">
                {agent.enabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Badge variant="outline" className="text-xs capitalize">{agent.review_mode}</Badge>
              {runtime && (
                <Badge
                  className={`text-xs border ${STATUS_STYLES[runtime.current_status] || 'bg-slate-100 text-slate-600'}`}
                  data-testid={`agent-status-${agent.agent_type}`}
                >
                  {runtime.current_status}
                </Badge>
              )}
              <span>Channels: {(agent.allowed_channels || []).join(', ') || 'none'}</span>
              <span>Max {agent.max_actions_per_lead_per_day}/day</span>
            </div>
            <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap" data-testid={`agent-schedule-${agent.agent_type}`}>
              <span><Clock className="w-3 h-3 inline mr-1" />Trigger: <span className="font-medium capitalize">{String(agent.trigger_event || 'lead_created').replace(/_/g, ' ')}</span></span>
              <span>Delay: {formatDelay(agent.initial_delay_minutes)}</span>
              <span>Repeat: {agent.repeat_frequency_hours ? `every ${agent.repeat_frequency_hours}h` : 'one-shot'}</span>
              <span>Max repeats: {agent.max_repeats ?? 1}</span>
            </div>
            {runtime && (
              <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-3 flex-wrap" data-testid={`agent-runtime-${agent.agent_type}`}>
                <span><Activity className="w-3 h-3 inline mr-1" />{runtime.actions_today} actions today</span>
                <span>{runtime.pending_actions} pending</span>
                <span>{runtime.completed_actions} sent</span>
                <span>{runtime.success_rate}% success</span>
                <span>{Number(runtime.credits_used_today || 0).toFixed(2)} credits used</span>
                {runtime.error_count > 0 && (
                  <span className="text-red-600">{runtime.error_count} errors</span>
                )}
              </div>
            )}
          </div>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid={`configure-${agent.agent_type}-btn`}>
              Configure
            </Button>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{AGENT_LABELS[agent.agent_type]}</DialogTitle>
                <DialogDescription>Configure this agent for {product.name}.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="flex items-center justify-between">
                  <Label>Enabled</Label>
                  <Switch
                    checked={!!cfg.enabled}
                    onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
                    data-testid={`agent-${agent.agent_type}-enabled`}
                  />
                </div>
                <div>
                  <Label>Review Mode</Label>
                  <div className="flex gap-2 mt-1">
                    {['manual', 'auto'].map(mode => (
                      <Button
                        key={mode}
                        type="button"
                        size="sm"
                        variant={cfg.review_mode === mode ? 'default' : 'outline'}
                        onClick={() => setCfg({ ...cfg, review_mode: mode })}
                      >
                        {mode === 'manual' ? 'Manual Review' : 'Auto Send'}
                      </Button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Allowed Channels</Label>
                  <div className="flex gap-2 mt-1">
                    {['email', 'sms'].map(ch => (
                      <Button
                        key={ch}
                        type="button"
                        size="sm"
                        variant={(cfg.allowed_channels || []).includes(ch) ? 'default' : 'outline'}
                        onClick={() => toggleChannel(ch)}
                      >
                        {ch.toUpperCase()}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <Label>Frequency (h)</Label>
                    <Input
                      type="number"
                      value={cfg.follow_up_frequency_hours}
                      onChange={(e) => setCfg({ ...cfg, follow_up_frequency_hours: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Max actions/day</Label>
                    <Input
                      type="number"
                      value={cfg.max_actions_per_lead_per_day}
                      onChange={(e) => setCfg({ ...cfg, max_actions_per_lead_per_day: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Max credits/day</Label>
                    <Input
                      type="number"
                      step="0.1"
                      value={cfg.max_credits_per_lead_per_day}
                      onChange={(e) => setCfg({ ...cfg, max_credits_per_lead_per_day: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Prompt Instructions</Label>
                  <Textarea
                    rows={4}
                    value={cfg.prompt_instructions || ''}
                    onChange={(e) => setCfg({ ...cfg, prompt_instructions: e.target.value })}
                    placeholder="Custom instructions for this agent (tone, style, what to emphasize...)"
                  />
                </div>

                {/* ============ Scheduling Visibility ============ */}
                <div className="pt-4 border-t">
                  <div className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-indigo-600" /> Scheduling
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <Label>Trigger Event</Label>
                      <select
                        className="w-full border rounded px-3 py-2 text-sm bg-white"
                        value={cfg.trigger_event || 'lead_created'}
                        onChange={(e) => setCfg({ ...cfg, trigger_event: e.target.value })}
                        data-testid={`agent-${agent.agent_type}-trigger`}
                      >
                        {TRIGGER_EVENTS.map((t) => (
                          <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <Label>Initial Delay (min)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={cfg.initial_delay_minutes ?? 0}
                        onChange={(e) => setCfg({ ...cfg, initial_delay_minutes: e.target.value })}
                        data-testid={`agent-${agent.agent_type}-delay`}
                      />
                    </div>
                    <div>
                      <Label>Repeat (h, blank = one-shot)</Label>
                      <Input
                        type="number"
                        min={0}
                        value={cfg.repeat_frequency_hours ?? ''}
                        placeholder="leave blank for one-shot"
                        onChange={(e) => setCfg({ ...cfg, repeat_frequency_hours: e.target.value })}
                        data-testid={`agent-${agent.agent_type}-repeat`}
                      />
                    </div>
                    <div>
                      <Label>Max Repeats</Label>
                      <Input
                        type="number"
                        min={1}
                        value={cfg.max_repeats ?? 1}
                        onChange={(e) => setCfg({ ...cfg, max_repeats: e.target.value })}
                        data-testid={`agent-${agent.agent_type}-maxrepeats`}
                      />
                    </div>
                  </div>
                  <div className="mt-3">
                    <Label className="text-xs uppercase tracking-wide text-slate-500">Stop Conditions</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {STOP_CONDITION_OPTIONS.map((opt) => {
                        const checked = (cfg.stop_conditions || []).includes(opt.value);
                        return (
                          <Button
                            key={opt.value}
                            type="button"
                            size="sm"
                            variant={checked ? 'default' : 'outline'}
                            onClick={() => {
                              const set = new Set(cfg.stop_conditions || []);
                              if (set.has(opt.value)) set.delete(opt.value);
                              else set.add(opt.value);
                              setCfg({ ...cfg, stop_conditions: Array.from(set) });
                            }}
                            data-testid={`stop-cond-${opt.value}`}
                          >
                            {opt.label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="save-agent-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </CardContent>
    </Card>
  );
}

// ============ AI GUIDANCE RULES ============

const AGENT_OPTIONS = Object.keys(AGENT_LABELS);

function GuidanceRulesTab({ product, reload, isAdmin }) {
  const rules = product.fit_rules || [];
  const sortedRules = [...rules].sort((a, b) => (b.priority || 50) - (a.priority || 50));
  const emptyForm = {
    rule_name: '',
    customer_goal: '',
    pain_point: '',
    recommended_feature: '',
    recommended_next_step: '',
    suggested_agent: '',
    suggested_message: '',
    priority: 50,
    is_active: true
  };
  const [form, setForm] = useState(emptyForm);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setOpen(true); };
  const openEdit = (rule) => {
    setForm({
      rule_name: rule.rule_name || '',
      customer_goal: rule.customer_goal || '',
      pain_point: rule.pain_point || '',
      recommended_feature: rule.recommended_feature || '',
      recommended_next_step: rule.recommended_next_step || '',
      suggested_agent: rule.suggested_agent || '',
      suggested_message: rule.suggested_message || '',
      priority: rule.priority ?? 50,
      is_active: rule.is_active !== false
    });
    setEditingId(rule.id);
    setOpen(true);
  };

  const save = async () => {
    if (!form.customer_goal || !form.recommended_feature || !form.recommended_next_step) {
      toast.error('Customer Goal, Recommended Feature and Next Step are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        priority: parseInt(form.priority, 10) || 50,
        pain_point: form.pain_point || null,
        suggested_agent: form.suggested_agent || null,
        suggested_message: form.suggested_message || null,
        rule_name: form.rule_name || null
      };
      if (editingId) {
        await productsApi.updateGuidanceRule(product.id, editingId, payload);
        toast.success('Guidance rule updated');
      } else {
        await productsApi.addGuidanceRule(product.id, payload);
        toast.success('Guidance rule added');
      }
      setForm(emptyForm);
      setEditingId(null);
      setOpen(false);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save guidance rule');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (ruleId) => {
    try {
      await productsApi.deleteGuidanceRule(product.id, ruleId);
      toast.success('Rule deleted');
      await reload();
    } catch {
      toast.error('Failed to delete rule');
    }
  };

  return (
    <div className="space-y-3" data-testid="guidance-rules-tab">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm text-slate-500 max-w-2xl">
            <span className="font-medium text-slate-700">AI Guidance Rules</span> map a customer's goal (and optional pain point) to a recommended feature, next step, and the agent that should act. The AI uses the highest-priority active rule that matches.
          </p>
          <p className="text-xs text-amber-700 mt-1">
            <AlertCircle className="w-3 h-3 inline mr-1" />
            Compliance: rules may only recommend features, workflows or educational next steps — never "buy", "trade", or guaranteed-profit language.
          </p>
        </div>
        {isAdmin && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(emptyForm); } }}>
            <Button onClick={openCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-guidance-rule-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Rule
            </Button>
            <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingId ? 'Edit AI Guidance Rule' : 'Add AI Guidance Rule'}</DialogTitle>
                <DialogDescription>Tell the AI how to respond when a lead matches this scenario.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label>Rule Name (optional)</Label>
                  <Input
                    value={form.rule_name}
                    onChange={(e) => setForm({ ...form, rule_name: e.target.value })}
                    placeholder="e.g. SaaS founder · save time"
                    data-testid="rule-name-input"
                  />
                </div>
                <div>
                  <Label>Customer Goal *</Label>
                  <Input
                    value={form.customer_goal}
                    onChange={(e) => setForm({ ...form, customer_goal: e.target.value })}
                    placeholder="e.g. save_time, generate_leads"
                    data-testid="rule-goal-input"
                  />
                </div>
                <div>
                  <Label>Pain Point (optional)</Label>
                  <Input
                    value={form.pain_point}
                    onChange={(e) => setForm({ ...form, pain_point: e.target.value })}
                    placeholder="e.g. manual_follow_up"
                    data-testid="rule-pain-input"
                  />
                </div>
                <div>
                  <Label>Recommended Feature / Service *</Label>
                  <Input
                    value={form.recommended_feature}
                    onChange={(e) => setForm({ ...form, recommended_feature: e.target.value })}
                    placeholder="e.g. AI Sequence Builder"
                    data-testid="rule-feature-input"
                  />
                </div>
                <div>
                  <Label>Recommended Next Step *</Label>
                  <Input
                    value={form.recommended_next_step}
                    onChange={(e) => setForm({ ...form, recommended_next_step: e.target.value })}
                    placeholder="e.g. Book a 15-min demo"
                    data-testid="rule-next-step-input"
                  />
                </div>
                <div>
                  <Label>Suggested Agent (optional)</Label>
                  <select
                    className="w-full border rounded px-3 py-2 text-sm bg-white"
                    value={form.suggested_agent}
                    onChange={(e) => setForm({ ...form, suggested_agent: e.target.value })}
                    data-testid="rule-agent-select"
                  >
                    <option value="">— No specific agent —</option>
                    {AGENT_OPTIONS.map((a) => (
                      <option key={a} value={a}>{AGENT_LABELS[a]}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Suggested Message (optional)</Label>
                  <Textarea
                    rows={3}
                    value={form.suggested_message}
                    onChange={(e) => setForm({ ...form, suggested_message: e.target.value })}
                    placeholder="Optional message template the AI may use as inspiration"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 items-end">
                  <div>
                    <Label>Priority (1-100)</Label>
                    <Input
                      type="number"
                      min={1}
                      max={100}
                      value={form.priority}
                      onChange={(e) => setForm({ ...form, priority: e.target.value })}
                      data-testid="rule-priority-input"
                    />
                  </div>
                  <div className="flex items-center gap-2 pb-2">
                    <Switch
                      checked={!!form.is_active}
                      onCheckedChange={(v) => setForm({ ...form, is_active: v })}
                      data-testid="rule-active-toggle"
                    />
                    <Label>Active</Label>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                <Button onClick={save} disabled={saving} data-testid="save-guidance-rule-btn">
                  <Save className="w-4 h-4 mr-2" />
                  {saving ? 'Saving...' : (editingId ? 'Save changes' : 'Add Rule')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {sortedRules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-500">
            <Target className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            No guidance rules yet. Add rules to help the AI recommend the right feature & next step.
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {sortedRules.map((r) => (
            <Card key={r.id} data-testid={`guidance-rule-${r.id}`} className={r.is_active === false ? 'opacity-60' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 text-sm space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {r.rule_name && (
                        <span className="font-semibold text-slate-800">{r.rule_name}</span>
                      )}
                      <Badge variant="outline" className="text-[10px]">P{r.priority ?? 50}</Badge>
                      {r.is_active === false && (
                        <Badge variant="secondary" className="text-[10px]">Inactive</Badge>
                      )}
                    </div>
                    <div><span className="text-slate-500 text-xs">Goal:</span> <span className="font-medium">{r.customer_goal}</span></div>
                    {r.pain_point && (
                      <div><span className="text-slate-500 text-xs">Pain:</span> {r.pain_point}</div>
                    )}
                    <div><span className="text-slate-500 text-xs">Recommend:</span> {r.recommended_feature}</div>
                    <div><span className="text-slate-500 text-xs">Next step:</span> {r.recommended_next_step}</div>
                    {r.suggested_agent && (
                      <div><span className="text-slate-500 text-xs">Agent:</span> {AGENT_LABELS[r.suggested_agent] || r.suggested_agent}</div>
                    )}
                    {r.suggested_message && (
                      <div className="text-xs text-slate-600 italic pt-2 border-t mt-2">
                        "{r.suggested_message}"
                      </div>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex flex-col gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`edit-guidance-rule-${r.id}`}>
                        <SettingsIcon className="w-4 h-4 text-slate-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => remove(r.id)} data-testid={`delete-guidance-rule-${r.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ WORKFLOW (read-only) ============

function WorkflowTab({ productId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await productsApi.workflow(productId);
      setData(r.data);
    } catch {
      toast.error('Failed to load workflow');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (loading) return <div className="p-4 text-sm text-slate-500">Loading workflow…</div>;
  if (!data) return null;

  const steps = data.steps || [];
  const branches = data.branch_hints || [];

  return (
    <div className="space-y-4" data-testid="workflow-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <WorkflowIcon className="w-4 h-4" /> Automation Flow (read-only)
          </CardTitle>
          <CardDescription>
            Auto-generated from this product's agent configuration and guidance rules. Toggle agents in the <span className="font-medium">Agents</span> tab to change the flow.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {data.sandbox_mode && (
            <div className="mb-4 px-3 py-2 rounded border border-amber-200 bg-amber-50 text-amber-800 text-xs flex items-center gap-2">
              <Beaker className="w-4 h-4" />
              Sandbox Mode is ON for this product — actions will be queued/logged but no real emails or SMS will be sent.
            </div>
          )}
          <div className="space-y-2">
            {steps.map((s, i) => {
              if (s.type === 'trigger') {
                return (
                  <div key={i} className="flex items-center gap-2">
                    <div className="px-3 py-2 rounded-full bg-blue-100 text-blue-800 text-xs font-medium border border-blue-200">
                      <Play className="w-3 h-3 inline mr-1" />
                      Trigger · {s.label}
                    </div>
                  </div>
                );
              }
              const isOff = !s.enabled;
              const delay = s.initial_delay_minutes || 0;
              return (
                <div key={i} data-testid={`workflow-step-${s.agent_type}`}>
                  {delay > 0 && !isOff && (
                    <div className="flex items-center gap-2 ml-1">
                      <ChevronRight className="w-4 h-4 text-slate-300" />
                      <div className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-[10px] font-medium border border-amber-200 inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Wait {formatDelay(delay)}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <ChevronRight className="w-4 h-4 text-slate-300 ml-1" />
                    <div className={`flex items-start gap-3 px-3 py-2 rounded-lg border w-full max-w-3xl ${
                      isOff ? 'bg-slate-50 border-slate-200 opacity-60' : 'bg-white border-indigo-200'
                    }`}>
                      <Bot className={`w-4 h-4 mt-0.5 ${isOff ? 'text-slate-400' : 'text-indigo-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{s.label}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 flex-wrap mt-0.5">
                          <Badge variant={isOff ? 'secondary' : 'default'} className="text-[10px]">
                            {isOff ? 'Disabled' : 'Enabled'}
                          </Badge>
                          <Badge variant="outline" className="text-[10px] capitalize">{s.review_mode}</Badge>
                          <span>Channels: {(s.channels || []).join(', ') || 'none'}</span>
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap mt-1">
                          <span><Clock className="w-3 h-3 inline mr-1" />Trigger: <span className="font-medium capitalize">{String(s.trigger_event || 'lead_created').replace(/_/g, ' ')}</span></span>
                          <span>Delay: {formatDelay(s.initial_delay_minutes)}</span>
                          <span>Repeat: {s.repeat_frequency_hours ? `every ${s.repeat_frequency_hours}h` : 'one-shot'}</span>
                          <span>Max repeats: {s.max_repeats ?? 1}</span>
                        </div>
                        {!isOff && (s.stop_conditions || []).length > 0 && (
                          <div className="text-[11px] text-slate-400 mt-1">
                            Stops on: {(s.stop_conditions || []).map((c) => String(c).replace(/_/g, ' ')).join(', ')}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Terminal node */}
            {steps.length > 1 && (
              <div className="flex items-center gap-2 pt-1">
                <ChevronRight className="w-4 h-4 text-slate-300 ml-1" />
                <div className="px-3 py-2 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium border border-emerald-200">
                  Stop · Lead replied / Demo booked / Deal closed / Opted out
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Branch Hints from Guidance Rules</CardTitle>
          <CardDescription>Top {branches.length} active rules influence what the AI recommends.</CardDescription>
        </CardHeader>
        <CardContent>
          {branches.length === 0 ? (
            <p className="text-sm text-slate-500">No active guidance rules. Add some under the AI Guidance Rules tab.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {branches.map((b, i) => (
                <li key={i} className="p-2 rounded border bg-slate-50">
                  <div className="text-xs text-slate-500">IF</div>
                  <div className="font-mono text-xs">{b.if}</div>
                  <div className="text-xs text-slate-500 mt-1">THEN recommend</div>
                  <div>
                    <span className="font-medium">{b.then_feature}</span>
                    <span className="text-slate-500"> → {b.then_next_step}</span>
                    {b.then_agent && <span className="text-slate-400"> · via {AGENT_LABELS[b.then_agent] || b.then_agent}</span>}
                    <Badge variant="outline" className="ml-2 text-[10px]">P{b.priority}</Badge>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ INTEGRATIONS ============

function IntegrationsTab({ product, events, reloadEvents }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [templates, setTemplates] = useState([]);
  const [leadForm, setLeadForm] = useState({
    name: 'Test Lead',
    email: `test+${Date.now()}@example.com`,
    phone: '',
    source: 'test_button',
    goal: 'save_time',
    pain_point: 'manual_follow_up'
  });
  const [eventForm, setEventForm] = useState({
    email: '',
    event_type: 'trial_started'
  });
  const [sendingLead, setSendingLead] = useState(false);
  const [sendingEvent, setSendingEvent] = useState(false);

  useEffect(() => {
    integrationsApi.eventTemplates().then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const sendTestLead = async () => {
    if (!leadForm.name || !leadForm.email) {
      toast.error('Name and email are required');
      return;
    }
    setSendingLead(true);
    try {
      const r = await integrationsApi.testLead({
        product_code: product.product_code,
        ...leadForm
      });
      toast.success(`Test lead delivered (${r.data?.lead_id ? 'created' : 'received'})`);
      // Refresh inbound events so user sees it
      reloadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test lead failed');
    } finally {
      setSendingLead(false);
    }
  };

  const sendTestEvent = async () => {
    if (!eventForm.email || !eventForm.event_type) {
      toast.error('Email and event type are required');
      return;
    }
    setSendingEvent(true);
    try {
      await integrationsApi.testEvent({
        product_code: product.product_code,
        email: eventForm.email,
        event_type: eventForm.event_type
      });
      toast.success('Test event sent');
      reloadEvents();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test event failed');
    } finally {
      setSendingEvent(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="integrations-tab">
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <TestTube2 className="w-4 h-4 text-amber-600" /> Test Integration Helpers
          </CardTitle>
          <CardDescription>
            Send a synthetic lead or event into this product to verify your workflow end-to-end.
            {product.sandbox_mode
              ? ' Sandbox Mode is ON — actions will be queued/logged without sending real messages.'
              : ' Sandbox Mode is OFF — agents will run as configured (real sends possible).'}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-6 text-sm">
          {/* Test Lead */}
          <div className="space-y-2" data-testid="test-lead-card">
            <div className="font-medium">Send Test Lead</div>
            <div className="grid grid-cols-2 gap-2">
              <Input
                placeholder="Name"
                value={leadForm.name}
                onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                data-testid="test-lead-name"
              />
              <Input
                placeholder="Email"
                value={leadForm.email}
                onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                data-testid="test-lead-email"
              />
              <Input
                placeholder="Phone (optional)"
                value={leadForm.phone}
                onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
              />
              <Input
                placeholder="Goal"
                value={leadForm.goal}
                onChange={(e) => setLeadForm({ ...leadForm, goal: e.target.value })}
              />
              <Input
                placeholder="Pain point"
                value={leadForm.pain_point}
                onChange={(e) => setLeadForm({ ...leadForm, pain_point: e.target.value })}
              />
              <Input
                placeholder="Source"
                value={leadForm.source}
                onChange={(e) => setLeadForm({ ...leadForm, source: e.target.value })}
              />
            </div>
            <Button
              size="sm"
              onClick={sendTestLead}
              disabled={sendingLead}
              className="bg-indigo-600 hover:bg-indigo-700 mt-1"
              data-testid="send-test-lead-btn"
            >
              <Beaker className="w-4 h-4 mr-2" />
              {sendingLead ? 'Sending…' : 'Send Test Lead'}
            </Button>
          </div>

          {/* Test Event */}
          <div className="space-y-2" data-testid="test-event-card">
            <div className="font-medium">Send Test Event</div>
            <Input
              placeholder="Lead email (must already exist for matching)"
              value={eventForm.email}
              onChange={(e) => setEventForm({ ...eventForm, email: e.target.value })}
              data-testid="test-event-email"
            />
            <select
              className="w-full border rounded px-3 py-2 text-sm bg-white"
              value={eventForm.event_type}
              onChange={(e) => setEventForm({ ...eventForm, event_type: e.target.value })}
              data-testid="test-event-type-select"
            >
              {(templates.length ? templates : [
                { event_type: 'trial_started', event_name: 'Trial Started' },
                { event_type: 'pricing_viewed', event_name: 'Pricing Page Viewed' },
                { event_type: 'demo_requested', event_name: 'Demo Requested' }
              ]).map((t) => (
                <option key={t.event_type} value={t.event_type}>{t.event_name}</option>
              ))}
            </select>
            <Button
              size="sm"
              onClick={sendTestEvent}
              disabled={sendingEvent || !eventForm.email || !eventForm.event_type}
              className="bg-indigo-600 hover:bg-indigo-700 mt-1"
              data-testid="send-test-event-btn"
            >
              <Beaker className="w-4 h-4 mr-2" />
              {sendingEvent ? 'Sending…' : 'Send Test Event'}
            </Button>
            <p className="text-[11px] text-slate-500 mt-1">
              Lead email must exist on this product so the event can be matched.
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Code className="w-4 h-4" /> Generic Integration Endpoints
          </CardTitle>
          <CardDescription>
            Push leads and events into the system from any external platform.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <div>
            <Label className="text-xs uppercase text-slate-500">Product Code</Label>
            <pre className="bg-slate-100 px-3 py-2 rounded text-xs font-mono">{product.product_code}</pre>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">POST /api/integrations/leads</Label>
            <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto mt-1">
{`curl -X POST ${backendUrl}/api/integrations/leads \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_code": "${product.product_code}",
    "name": "John Smith",
    "email": "john@example.com",
    "phone": "+1...",
    "source": "trial_signup",
    "goal": "save_time",
    "pain_point": "manual_follow_up",
    "metadata": {}
  }'`}
            </pre>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">POST /api/integrations/events</Label>
            <pre className="bg-slate-900 text-slate-100 p-3 rounded text-xs overflow-x-auto mt-1">
{`curl -X POST ${backendUrl}/api/integrations/events \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_code": "${product.product_code}",
    "email": "john@example.com",
    "event_type": "trial_started",
    "event_name": "Trial Started",
    "metadata": {}
  }'`}
            </pre>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base">Recent Events</CardTitle>
          <Button size="sm" variant="outline" onClick={reloadEvents}>Refresh</Button>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <div className="text-center text-slate-500 py-8 text-sm">
              <Activity className="w-8 h-8 mx-auto mb-2 text-slate-300" />
              No events received yet.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="text-left py-2">Event</th>
                  <th className="text-left py-2">Email</th>
                  <th className="text-left py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {events.map(e => (
                  <tr key={e.id} className="border-t">
                    <td className="py-2">{e.event_name || e.event_type}</td>
                    <td className="py-2 text-slate-600">{e.email}</td>
                    <td className="py-2 text-xs text-slate-500">
                      {new Date(e.created_at).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ============ SETTINGS ============

function SettingsTab({ product, reload, isAdmin }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { setForm(product); }, [product]);

  const save = async () => {
    setSaving(true);
    try {
      await productsApi.update(product.id, {
        name: form.name,
        product_code: form.product_code,
        category: form.category,
        website_url: form.website_url,
        description: form.description,
        target_audience: form.target_audience,
        trial_available: form.trial_available,
        trial_length_days: form.trial_length_days ? parseInt(form.trial_length_days, 10) : null,
        default_funnel: form.default_funnel,
        default_cta: form.default_cta,
        compliance_text: form.compliance_text,
        is_active: form.is_active,
        sandbox_mode: form.sandbox_mode
      });
      toast.success('Product updated');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await productsApi.delete(product.id);
      toast.success('Product deleted');
      navigate('/products');
    } catch {
      toast.error('Failed to delete');
    }
  };

  return (
    <Card data-testid="settings-tab">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <SettingsIcon className="w-4 h-4" /> Product Settings
        </CardTitle>
      </CardHeader>
      <CardContent className="grid grid-cols-2 gap-4">
        <div>
          <Label>Name</Label>
          <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} disabled={!isAdmin} />
        </div>
        <div>
          <Label>Product Code</Label>
          <Input value={form.product_code || ''} onChange={(e) => setForm({ ...form, product_code: e.target.value })} disabled={!isAdmin} />
        </div>
        <div>
          <Label>Category</Label>
          <Input value={form.category || ''} onChange={(e) => setForm({ ...form, category: e.target.value })} disabled={!isAdmin} />
        </div>
        <div>
          <Label>Website URL</Label>
          <Input value={form.website_url || ''} onChange={(e) => setForm({ ...form, website_url: e.target.value })} disabled={!isAdmin} />
        </div>
        <div className="col-span-2">
          <Label>Description</Label>
          <Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} disabled={!isAdmin} />
        </div>
        <div className="col-span-2">
          <Label>Target Audience</Label>
          <Input value={form.target_audience || ''} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} disabled={!isAdmin} />
        </div>
        <div className="flex items-center gap-3">
          <Switch checked={!!form.trial_available} onCheckedChange={(v) => setForm({ ...form, trial_available: v })} disabled={!isAdmin} />
          <Label>Trial Available</Label>
        </div>
        <div>
          <Label>Trial Length (days)</Label>
          <Input type="number" value={form.trial_length_days || ''} onChange={(e) => setForm({ ...form, trial_length_days: e.target.value })} disabled={!isAdmin || !form.trial_available} />
        </div>
        <div>
          <Label>Default Funnel</Label>
          <Input value={form.default_funnel || ''} onChange={(e) => setForm({ ...form, default_funnel: e.target.value })} disabled={!isAdmin} />
        </div>
        <div>
          <Label>Default CTA</Label>
          <Input value={form.default_cta || ''} onChange={(e) => setForm({ ...form, default_cta: e.target.value })} disabled={!isAdmin} />
        </div>
        <div className="col-span-2">
          <Label>Compliance / Disclaimer</Label>
          <Textarea rows={2} value={form.compliance_text || ''} onChange={(e) => setForm({ ...form, compliance_text: e.target.value })} disabled={!isAdmin} />
        </div>
        <div className="flex items-center gap-3 col-span-2">
          <Switch checked={!!form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} disabled={!isAdmin} />
          <Label>Active</Label>
        </div>
        <div className="flex items-center gap-3 col-span-2">
          <Switch
            checked={!!form.sandbox_mode}
            onCheckedChange={(v) => setForm({ ...form, sandbox_mode: v })}
            disabled={!isAdmin}
            data-testid="settings-sandbox-toggle"
          />
          <div>
            <Label className="flex items-center gap-1"><Beaker className="w-3 h-3 text-amber-600" /> Sandbox Mode</Label>
            <p className="text-xs text-slate-500">When ON, AI actions are queued/logged but no real emails or SMS are sent.</p>
          </div>
        </div>
        {isAdmin && (
          <div className="col-span-2 flex items-center justify-between pt-3 border-t">
            <Button variant="destructive" size="sm" onClick={() => setConfirmDelete(true)} data-testid="delete-product-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Delete Product
            </Button>
            <Button onClick={save} disabled={saving} data-testid="save-product-settings-btn">
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        )}
        <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this product?</DialogTitle>
              <DialogDescription>
                This will permanently delete the product. Leads currently assigned to it will become "Unassigned" but won't be deleted.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(false)}>Cancel</Button>
              <Button variant="destructive" onClick={remove}>Delete</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
